import React from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Alert,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import GroupOptions from '../common/GroupOptions'
import TagsEdit from '../common/TagsEdit'
import Feature from '../common/Feature'

HighlightForm.propTypes = {
  value: PropTypes.object,
  tags: PropTypes.array,
  stats: PropTypes.array,
  tagOptions: PropTypes.array,
  statOptions: PropTypes.array,
  groups: PropTypes.array,
  username: PropTypes.string,
  players: PropTypes.array,
  onNameChange: PropTypes.func,
  onDescriptionChange: PropTypes.func,
  onStartChange: PropTypes.func,
  onEndChange: PropTypes.func,
  onGroupChange: PropTypes.func,
  onPlayerChange: PropTypes.func,
  onTagsChange: PropTypes.func,
  onStatsChange: PropTypes.func,
  nameError: PropTypes.bool,
  rangeError: PropTypes.bool
}

const numberValue = value => (
  value === undefined || value === null ? '' : value
)

const formatTimecode = seconds => {
  if (!Number.isFinite(seconds)) return '—'
  const minutes = Math.floor(seconds / 60)
  const remainder = (seconds % 60).toFixed(1).padStart(4, '0')
  return `${String(minutes).padStart(2, '0')}:${remainder}`
}

export default function HighlightForm ({
  value,
  tags,
  stats,
  tagOptions = [],
  statOptions = [],
  groups,
  username,
  players = [],
  onNameChange,
  onDescriptionChange,
  onStartChange,
  onEndChange,
  onGroupChange,
  onPlayerChange,
  onTagsChange,
  onStatsChange,
  nameError = false,
  rangeError = false
}) {
  const parseSeconds = raw => (
    raw === '' || raw === undefined || raw === null ? Number.NaN : Number(raw)
  )
  const start = parseSeconds(value.start)
  const end = parseSeconds(value.end)
  const duration = Number.isFinite(start) && Number.isFinite(end) && end > start
    ? (end - start).toFixed(1)
    : null

  return (
    <Stack spacing={2.25}>
      <TextField
        id="name"
        label="Highlight name"
        autoFocus
        fullWidth
        value={value.name || ''}
        onChange={onNameChange}
        error={nameError}
        helperText={nameError ? 'Give this highlight a name.' : 'Use a short name you can scan quickly later.'}
      />

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          bgcolor: 'rgba(255,255,255,.018)'
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography
                variant="caption"
                color="primary.light"
                sx={{ fontWeight: 750, letterSpacing: '.12em' }}
              >
                CLIP RANGE
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fine tune the marked in and out points.
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 750, fontVariantNumeric: 'tabular-nums' }}
              >
                {duration ? `${duration}s` : '—'}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatTimecode(start)} → {formatTimecode(end)}
              </Typography>
            </Box>
          </Stack>

          <Grid container spacing={1.25}>
            <Grid item xs={6}>
              <TextField
                id="start"
                label="In"
                type="number"
                fullWidth
                value={numberValue(value.start)}
                onChange={onStartChange}
                error={rangeError}
                inputProps={{ step: 0.1, min: 0 }}
                helperText="seconds"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                id="end"
                label="Out"
                type="number"
                fullWidth
                value={numberValue(value.end)}
                onChange={onEndChange}
                error={rangeError}
                inputProps={{ step: 0.1, min: 0 }}
                helperText={rangeError ? 'Out must be after In' : 'seconds'}
              />
            </Grid>
          </Grid>
        </Stack>
      </Paper>

      <TextField
        id="description"
        label="Notes"
        multiline
        minRows={2}
        maxRows={5}
        fullWidth
        value={value.description || ''}
        onChange={onDescriptionChange}
        placeholder="What happened in this clip?"
      />

      <Divider />

      <Stack spacing={1.5}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 750, letterSpacing: '.1em' }}
          >
            ORGANIZE
          </Typography>
        </Box>

        {value.playerLinkEditable === false
          ? value.playerLinked && <Alert severity="info">This highlight is linked to a player profile on another account. Your edits will keep that link.</Alert>
          : <TextField select fullWidth label="Featured player" value={value.playerId || ''} onChange={onPlayerChange}
              helperText={players.length ? 'Link this highlight to one of your player profiles.' : 'Add a player profile to link this highlight.'}>
              <MenuItem value="">No player linked</MenuItem>
              {players.map(player => <MenuItem key={player.id} value={player.id}>{player.name}</MenuItem>)}
            </TextField>}

        <TagsEdit
          tags={tags}
          options={tagOptions}
          onChange={onTagsChange}
          label="Tags"
          placeholder="Type a tag and press Enter"
        />

        <Feature flag={Feature.STATS} username={username}>
          <TagsEdit
            tags={stats}
            options={statOptions}
            onChange={onStatsChange}
            label="Stats"
            placeholder="Type a stat and press Enter"
          />
        </Feature>
      </Stack>

      <Divider />

      <Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1, fontWeight: 750, letterSpacing: '.1em' }}
        >
          ACCESS
        </Typography>
        <GroupOptions
          groups={groups || []}
          selectedGroups={value.groups || []}
          onGroupChange={onGroupChange}
        />
      </Box>
    </Stack>
  )
}
